import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Caravan, Loader2, AlertCircle } from 'lucide-react';
import { useAuthStore, homePathFor } from '../store/useAuthStore';

const MIN_PASSWORD = 10;

export default function RegisterPage() {
  const navigate = useNavigate();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const register = useAuthStore((s) => s.register);

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (status === 'signed_in' && user) return <Navigate to={homePathFor(user.role)} replace />;

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const onSubmit = async (event) => {
    event.preventDefault();
    if (form.password.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters`);
      return;
    }
    setSubmitting(true);
    setError(null);

    const result = await register(form);
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }
    navigate(homePathFor('customer'), { replace: true });
  };

  return (
    <div className="grid min-h-full place-items-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="mb-3 grid h-12 w-12 place-items-center rounded-xl bg-brand-600 text-white">
            <Caravan className="h-6 w-6" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Create your account</h1>
          <p className="mt-1 text-sm text-slate-500">Reserve a space and manage your storage online.</p>
        </div>

        <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          {error && (
            <p role="alert" className="mb-4 flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-xs font-medium text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              {error}
            </p>
          )}

          <div className="mb-4 grid grid-cols-2 gap-3">
            <div>
              <label className="field-label mb-1" htmlFor="first_name">First name</label>
              <input id="first_name" required autoFocus className="field-input" value={form.first_name} onChange={set('first_name')} />
            </div>
            <div>
              <label className="field-label mb-1" htmlFor="last_name">Last name</label>
              <input id="last_name" required className="field-input" value={form.last_name} onChange={set('last_name')} />
            </div>
          </div>

          <label className="field-label mb-1" htmlFor="email">Email</label>
          <input id="email" type="email" autoComplete="email" required className="field-input mb-4" value={form.email} onChange={set('email')} />

          <label className="field-label mb-1" htmlFor="phone">Phone (optional)</label>
          <input id="phone" type="tel" autoComplete="tel" className="field-input mb-4" value={form.phone} onChange={set('phone')} />

          <label className="field-label mb-1" htmlFor="password">Password</label>
          <input
            id="password" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD}
            className="field-input" value={form.password} onChange={set('password')}
            aria-describedby="password-hint"
          />
          <p id="password-hint" className="mb-5 mt-1 text-[12px] text-slate-500">
            At least {MIN_PASSWORD} characters. A short phrase beats a short scramble.
          </p>

          <button type="submit" disabled={submitting} className="btn-primary w-full py-2.5">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {submitting ? 'Creating…' : 'Create account'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-slate-600">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-brand-700 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
